<template>
  <BaseLayout :heading="'Verbal Fluency - Letter F - Interview'" />

  <div class="instructions">
    <div class="line">I am going to say a letter of the alphabet. When I say begin, I want you to <b>Press the continue button</b> and tell me as many words as you can think of with that letter. You will have 60 seconds before I tell you to stop. None of the words can be names of people, or places, or numbers. For example, if I gave you the letter T, you could say take, toy, tooth, and so forth, but you should not say Tom because that is a person's name, you should not say Texas because that is the name of a place, and you should not say twelve because that is a number. Also, do not give me the same word with different endings. For example, if you say take, you should not also say takes and taking. Do you have any questions?</div>
    <div class="line">The letter is F. As in A-B-C-D-E…F.  Ready? <b>Press continue and begin.</b></div>
    <div class="line">(Wait for the time's up message after one minute. If there has been a 15 second pause with no response, say "keep going, the letter F")</div>
    <div class="line">That is fine. <b>Press the continue button.</b></div>
  </div>

  <VerbalFluencyInterview
    :jsonData="jsonData"
    :audioFile="audioFile"
    :visitId="visitId"
    taskType="Verbal Fluency - Letter F"
    @update:enteredWord="updateEnteredWord"
    @update:selectedAnswer="updateSelectedAnswer"
    @entrySummaryUpdated="updateEntrySummary"
    @update:suggestedWords="updateSuggestedWords"
  />

  <NotesSection 
    :administeringNotes="jsonData ? jsonData.administeringNotes : ''" 
    :scoringNotes="jsonData ? jsonData.scoringNotes : ''"
    :visitId="visitId"
    :taskType="'Verbal Fluency - Letter F'"
    ref="notesSection" />

  <TextButton buttonText="Submit" :dark="true" type="submit" @click="submitForm" />

  <NotificationPopup 
    :message="notificationMessage" 
    :visible="notificationVisible"
  />

  <ConfirmationPopup
    :visible="showConfirmPopup"
    @confirm="handleConfirmLeave"
    @noSave="handleNoSaveLeave"
    @cancel="showConfirmPopup = false"
  />
  
  <div v-if="loading" class="audio-status">Loading audio recording...</div>
  
  <div v-if="!loading" class="audio-player-container">
    <div v-if="error" class="audio-status error">{{ error }}</div>
    <div v-else-if="!audioUrl && !audioStatusDismissed" class="audio-status">
      <span>No audio recording available for this participant.</span>
      <button class="audio-status-close" @click="audioStatusDismissed = true" aria-label="Dismiss">&times;</button>
    </div>
    
    <AudioPlayer 
      v-else
      :audioUrl="audioUrl" 
      taskTitle="Letter F"
      @close="closeAudioPlayer"
    />
  </div>
</template>

<script>
import BaseLayout from '../BaseLayout.vue';
import VerbalFluencyInterview from '@/components/Pages/VerbalFluencyInterview.vue';
import TaskController from '../../controllers/TaskController';
import ServerCodeModel from '../../models/ServerCodeModel';
import NotificationPopup from '@/components/NotificationPopup.vue';
import ConfirmationPopup from '@/components/ConfirmationPopup.vue';
import NotesSection from '@/components/NotesSection.vue';
import TextButton from '@/components/TextButton.vue';
import AudioPlayer from '@/components/AudioPlayer.vue';
import ACTApiService from '@/services/ACTApiService.js';

export default {
  components: {
    BaseLayout,
    VerbalFluencyInterview,
    NotificationPopup,
    ConfirmationPopup,
    NotesSection,
    TextButton,
    AudioPlayer
  },
  data() {
    return {
      controller: null,
      jsonData: null,
      notificationMessage: '',
      notificationVisible: false,
      hasUnsavedChanges: false,
      showConfirmPopup: false,
      nextRoute: null,
      forceNavigate: false,
      audioUrl: null,
      audioStatusDismissed: false,
      audioFile: null,
      loading: false,
      error: null,
      actApiService: new ACTApiService(),
      transcriptWords: [],
      loadingTranscript: false,
      transcriptError: null
    };
  },
  computed: {
    visitId() {
      return localStorage.getItem('visitId');
    },
    isAdministering() {
        return this.$route.query.selection === 'administer';
    }
  },
  async mounted() {
    const serverCodeModel = new ServerCodeModel();
    serverCodeModel.logType = 'InterviewerVerbalFluencyLetterFLog';

    this.controller = new TaskController(serverCodeModel.logType, 'verbalFluencyLetterFData');

    await this.loadData();
    
    await this.fetchLogAudio();
    
    await this.fetchTranscript();
    
    // Set timestamps
    if (this.jsonData.timeCreated === '') {
        this.jsonData.timeCreated = new Date().toUTCString(); // Set timeCreated to current UTC time
    }
    this.jsonData.timeLastModified = new Date().toUTCString(); // Set timeLastModified to current UTC time
  },
  methods: {
    generateEntrySummary() {
      // Create a new summary object
      const summary = {
        c: 0,  // correct
        i: 0,  // incorrect
        r: 0,  // repeat
        q: 0,  // query
        ir: 0  // Count any that might have "ir" value
      };
      
      // Count occurrences of each selectedAnswer value
      if (this.jsonData && this.jsonData.entries) {
        this.jsonData.entries.forEach(entry => {
          const selected = (entry.selectedAnswer || '').toLowerCase();
          if (!selected) return;

          const hasI = selected.includes('i');
          const hasR = selected.includes('r');
          const hasQ = selected.includes('q');
          const hasC = selected.includes('c');

          if (hasI && hasR) {
            summary.ir++;
          } else if (hasI) {
            summary.i++;
          } else if (hasR) {
            summary.r++;
          } else if (hasC) {
            summary.c++;
          }

          if (hasQ) {
            summary.q++;
          }
        });
      }
      
      return summary;
    },
    updateEnteredWord(index, value) {
      if (this.jsonData && this.jsonData.entries[index]) {
        this.jsonData.entries[index].enteredWord = value;
        this.hasUnsavedChanges = true;
      }
    },
    updateSelectedAnswer(index, value) {
      if (this.jsonData && this.jsonData.entries[index]) {
        this.jsonData.entries[index].selectedAnswer = value;
        this.hasUnsavedChanges = true;
      }
    },
    async loadData() {
      await this.controller.loadData();
      this.jsonData = this.controller.model.data;
      this.hasUnsavedChanges = false;
    
      // Initialize entries if they don't exist
      if (!this.jsonData.entries) {
        this.jsonData.entries = Array.from({ length: 60 }, (_, i) => ({
          entryId: i + 1,
          enteredWord: '',
          selectedAnswer: null
        }));
      }
    },
    async submitForm() {
      this.notificationMessage = 'Submitting...';
      this.notificationVisible = true;
      
      try {
        // Validate data if needed
        if (!this.jsonData) {
          throw new Error('No data available to submit');
        }
        
        // Get the latest notes from the notes section
        const notesSection = this.$refs.notesSection;
        if (this.isAdministering) {
          this.jsonData.administeringNotes = notesSection.getAdministeringNotes();
        } else {
          this.jsonData.scoringNotes = notesSection.getScoringNotes();
        }
        
        this.jsonData.entrySummary = this.calculateEntrySummary();
        
        if (this.jsonData.timeCreated === '') {
          this.jsonData.timeCreated = new Date().toUTCString();
        }
        this.jsonData.timeLastModified = new Date().toUTCString();
        
        console.log('Saving Letter F data:', this.jsonData);
        
        await this.controller.model.saveData(this.jsonData);
        
        const subjectId = localStorage.getItem('currentSubjectId') || localStorage.getItem('subjectId');
        if (subjectId) {
          const allData = JSON.parse(localStorage.getItem('subjectData') || '{}');
          if (!allData[subjectId]) {
            allData[subjectId] = {};
          }
          
          allData[subjectId].letterFData = this.jsonData;
          allData[subjectId].verbalFluencyLetterFData = this.jsonData;
          
          localStorage.setItem('subjectData', JSON.stringify(allData));
        }
        
        await this.controller.submitForm();
        
        this.notificationMessage = 'Submitted Successfully!';
        this.hasUnsavedChanges = false;
      } catch (error) {
        this.notificationMessage = 'Submitting failed. Please try again.';
        console.error('Error submitting form:', error);
      } finally {
        this.resetNotification();
      }
    },
    resetNotification() {
      setTimeout(() => {
        this.notificationVisible = false;
      }, 3000);
    },
    async handleConfirmLeave() {
      this.showConfirmPopup = false;
      this.hasUnsavedChanges = false;
      await this.submitForm();
      this.$router.push(this.nextRoute);
    },
    handleNoSaveLeave() {
      this.showConfirmPopup = false;
      this.forceNavigate = true;
      this.hasUnsavedChanges = false;
      this.$router.push(this.nextRoute);
    },
    calculateEntrySummary() {
      const summary = { c: 0, i: 0, r: 0, q: 0, ir: 0 };
      
      if (this.jsonData && this.jsonData.entries) {
        this.jsonData.entries.forEach(entry => {
          const selected = (entry.selectedAnswer || '').toLowerCase();
          if (!selected) return;

          const hasI = selected.includes('i');
          const hasR = selected.includes('r');
          const hasQ = selected.includes('q');
          const hasC = selected.includes('c');

          if (hasI && hasR) {
            summary.ir++;
          } else if (hasI) {
            summary.i++;
          } else if (hasR) {
            summary.r++;
          } else if (hasC) {
            summary.c++;
          }

          if (hasQ) {
            summary.q++;
          }
        });
      }
      
      console.log('Calculated summary:', summary);
      return summary;
    },
    updateEntrySummary(summary) {
      if (this.jsonData) {
        this.jsonData.entrySummary = summary;
        this.hasUnsavedChanges = true;
      }
    },
    async fetchLogAudio() {
      this.loading = true;
      this.error = null;
      
      const logId = localStorage.getItem('verbalFluencyLetterFLogId');
      
      if (!logId) {
        this.loading = false;
        console.log('No Verbal Fluency Letter F audio log ID found');
        return; 
      }
      
      try {
        console.log('Fetching Letter F audio from log ID:', logId);
        const audioBlob = await this.actApiService.getLogBinary(logId);
        
        if (!audioBlob || audioBlob.size === 0) {
          throw new Error('Audio recording is empty or not available');
        }
        
        this.audioUrl = URL.createObjectURL(audioBlob);
        console.log('Created audio URL from log:', this.audioUrl);
      } catch (error) {
        console.error('Error fetching Letter F audio from logs:', error);
        this.error = 'No audio recording found for this participant.';
        this.audioUrl = null;
      } finally {
        this.loading = false;
      }
    },
    closeAudioPlayer() {
      this.audioUrl = null;
    },
    updateSuggestedWords(words) {
      if (this.jsonData) {
        // Create a new object to avoid direct mutation
        this.jsonData = {
          ...this.jsonData,
          suggestedWords: words
        };
        
        // Save the data to ensure suggestions are persisted
        this.controller.model.saveData(this.jsonData);
        console.log('Saved suggested words to data:', words);
      }
    },
    async fetchTranscript() {
      this.loadingTranscript = true;
      this.transcriptError = null;
      
      const logId = localStorage.getItem('verbalFluencyLetterFLogId');
      
      if (!logId) {
        this.loadingTranscript = false;
        console.log('No Verbal Fluency Letter F log ID found for transcript');
        return;
      }
      
      try {
        console.log('Fetching Letter F transcript from log ID:', logId);
        const response = await this.actApiService.getLogTranscript(logId);
        
        if (response && response.Value && response.Value.Words) {
          this.transcriptWords = response.Value.Words
            .map(item => item.Word)
            .filter(word => word.toLowerCase().startsWith('f'))
            .filter(word => word.length >= 2) // Filter out very short words
            .filter(word => !this.isCommonWord(word));
          
          console.log('Fetched F-words from transcript:', this.transcriptWords);
          
          if (this.transcriptWords.length > 0) {
            this.updateSuggestedWords(this.transcriptWords);
          }
        } else {
          console.log('Transcript data does not contain expected Word structure');
        }
      } catch (error) {
        console.error('Error fetching Letter F transcript:', error);
        this.transcriptError = 'Failed to load word suggestions.';
      } finally {
        this.loadingTranscript = false;
      }
    },
    isCommonWord(word) {
      const commonWords = [
        'i', 'me', 'my', 'myself', 'we', 'our', 'ours', 'ourselves', 'you', 'your', 'yours',
        'yourself', 'yourselves', 'he', 'him', 'his', 'himself', 'she', 'her', 'hers',
        'herself', 'it', 'its', 'itself', 'they', 'them', 'their', 'theirs', 'themselves',
        'what', 'which', 'who', 'whom', 'this', 'that', 'these', 'those', 'am', 'is', 'are',
        'was', 'were', 'be', 'been', 'being', 'have', 'has', 'had', 'having', 'do', 'does',
        'did', 'doing', 'a', 'an', 'the', 'and', 'but', 'if', 'or', 'because', 'as', 'until',
        'while', 'of', 'at', 'by', 'for', 'with', 'about', 'against', 'between', 'into',
        'through', 'during', 'before', 'after', 'above', 'below', 'to', 'from', 'up', 'down',
        'in', 'out', 'on', 'off', 'over', 'under', 'again', 'further', 'then', 'once', 'here',
        'there', 'when', 'where', 'why', 'how', 'all', 'any', 'both', 'each', 'few', 'more',
        'most', 'other', 'some', 'such', 'no', 'nor', 'not', 'only', 'own', 'same', 'so',
        'than', 'too', 'very', 's', 't', 'can', 'will', 'just', 'don', 'don\'t', 'should',
        'should\'ve', 'now', 'd', 'll', 'm', 'o', 're', 've', 'y', 'ain', 'aren', 'aren\'t',
        'couldn', 'couldn\'t', 'didn', 'didn\'t', 'doesn', 'doesn\'t', 'hadn', 'hadn\'t',
        'hasn', 'hasn\'t', 'haven', 'haven\'t', 'isn', 'isn\'t', 'ma', 'mightn', 'mightn\'t',
        'mustn', 'mustn\'t', 'needn', 'needn\'t', 'shan', 'shan\'t', 'shouldn', 'shouldn\'t',
        'wasn', 'wasn\'t', 'weren', 'weren\'t', 'won', 'won\'t', 'wouldn', 'wouldn\'t',
        'go', 'going', 'know', 'right', 'think', 'oh', 'okay', 'said', 'say', 'saying',
        'yeah', 'yes', 'come', 'like', 'um', 'uh', 'would', 'time', 'continue', 'ahead',
        'hit', 'let\'s', 'see'
      ];
      
      return commonWords.includes(word.toLowerCase());
    }
  },
  beforeRouteLeave(to, from, next) {
    const notesSection = this.$refs.notesSection;
    const currentAdministeringNotes = notesSection.getAdministeringNotes();
    const currentScoringNotes = notesSection.getScoringNotes();

    if (this.forceNavigate) {
      this.forceNavigate = false;
      this.hasUnsavedChanges = false;
      next();
      return;
    }

    if ( 
      (this.isAdministering && currentAdministeringNotes !== this.jsonData.administeringNotes) || 
      (!this.isAdministering && currentScoringNotes !== this.jsonData.scoringNotes) 
    ) {
      this.hasUnsavedChanges = true;
    }

    if (this.hasUnsavedChanges) {
      this.showConfirmPopup = true;
      this.nextRoute = to.fullPath;
      next(false);
    } else {
      next();
    }
  },
  beforeUnmount() {
    // Clean up object URL to avoid memory leaks
    if (this.audioUrl && this.audioUrl.startsWith('blob:')) {
      URL.revokeObjectURL(this.audioUrl);
    }
  }
};
</script>

<style scoped>
.instructions {
  font-size: 18px;
}
.line {
  margin-bottom: 20px;
}
.audio-status {
  margin: 20px 0;
  padding: 10px;
  background-color: #f5f5f5;
  border-radius: 4px;
  font-style: italic;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
}
.audio-status-close {
  background: none;
  border: 1px solid #bbb;
  border-radius: 4px;
  font-size: 16px;
  font-style: normal;
  color: #555;
  cursor: pointer;
  line-height: 1;
  padding: 1px 6px;
  flex-shrink: 0;
}
.audio-status-close:hover {
  background: #e0e0e0;
  color: #111;
}
.audio-status.error {
  background-color: #ffebee;
  color: #c62828;
}
.audio-player-container {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  background-color: #f5f5f5;
  padding: 15px;
  box-shadow: 0 -2px 4px rgba(0,0,0,0.1);
  z-index: 1000;
}
</style>